import { useState, useRef } from 'preact/hooks';
import type { Service } from './ServerCard';

interface ServiceListProps {
  services: Service[];
  onChange: (services: Service[]) => void;
}

export function ServiceList({ services, onChange }: ServiceListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const sorted = [...services].sort((a, b) => a.sort_order - b.sort_order);

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragOver = (e: DragEvent, index: number) => {
    e.preventDefault();
    dragOverItem.current = index;
  };

  const handleDrop = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const updated = [...sorted];
    const [removed] = updated.splice(dragItem.current, 1);
    updated.splice(dragOverItem.current, 0, removed);

    // Reassign sort_order
    const reordered = updated.map((svc, i) => ({ ...svc, sort_order: i }));
    onChange(reordered);

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDelete = (id: string) => {
    onChange(services.filter((s) => s.id !== id));
  };

  const handleInlineEdit = (id: string, field: 'name' | 'group_name', value: string) => {
    onChange(
      services.map((s) => (s.id === id ? { ...s, [field]: value || null } : s))
    );
  };

  return (
    <div class="service-list">
      {sorted.map((svc, index) => (
        <div
          key={svc.id}
          class="service-item"
          draggable
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e: DragEvent) => handleDragOver(e, index)}
          onDrop={handleDrop}
        >
          <span class="drag-handle">⠿</span>

          {editingId === svc.id ? (
            <div class="service-edit">
              <input
                class="input-inline"
                value={svc.name}
                onInput={(e) =>
                  handleInlineEdit(svc.id, 'name', (e.target as HTMLInputElement).value)
                }
                placeholder="Service-Name"
              />
              <input
                class="input-inline input-group"
                value={svc.group_name ?? ''}
                onInput={(e) =>
                  handleInlineEdit(svc.id, 'group_name', (e.target as HTMLInputElement).value)
                }
                placeholder="Gruppe"
              />
              <button class="btn-icon" onClick={() => setEditingId(null)} title="Fertig">
                ✓
              </button>
            </div>
          ) : (
            <div class="service-display" onClick={() => setEditingId(svc.id)}>
              <span class="service-name">{svc.name}</span>
              {svc.group_name && <span class="group-badge">{svc.group_name}</span>}
              {svc.is_restartable ? (
                <span class="restart-indicator" title="Restartbar">🔄</span>
              ) : null}
            </div>
          )}

          <button
            class="btn-icon btn-delete"
            onClick={() => handleDelete(svc.id)}
            title="Entfernen"
          >
            ✕
          </button>
        </div>
      ))}

      {sorted.length === 0 && (
        <p class="empty-hint">Keine Services konfiguriert.</p>
      )}
    </div>
  );
}
